/**
 * Storage is always UTC; rendering and "today" boundaries are computed in the
 * user's timezone (Africa/Lagos by default, per orgs.timezone).
 * No timezone library: Intl covers everything we need.
 */

export const LAGOS_TZ = 'Africa/Lagos';

/** Offset of `tz` from UTC at `instant`, in milliseconds. Lagos is fixed +1h (no DST). */
export function tzOffsetMs(instant: Date, tz: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts: Partial<Record<Intl.DateTimeFormatPartTypes, string>> = {};
  for (const p of dtf.formatToParts(instant)) parts[p.type] = p.value;
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second),
  );
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** A real calendar date: month 1-12, and a day that month of that year actually has. */
export function isRealDate(y: number, m: number, d: number): boolean {
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

/** "YYYY-MM-DD" naming a day that exists: 2026-02-28 yes, 2026-02-31 no. */
export function isIsoDate(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s) && isRealDate(Number(s.slice(0, 4)), Number(s.slice(5, 7)), Number(s.slice(8, 10)));
}

/** Calendar date ("YYYY-MM-DD") of `instant` as seen in `tz`. */
export function localDateOf(instant: Date, tz: string = LAGOS_TZ): string {
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return dtf.format(instant);
}

/**
 * UTC instants bounding the local calendar day containing `instant` in `tz`.
 * `start` is inclusive local midnight; `end` is exclusive next local midnight.
 */
export function dayBoundsUtc(instant: Date, tz: string = LAGOS_TZ): { start: Date; end: Date } {
  const [y, m, d] = localDateOf(instant, tz).split('-').map(Number) as [number, number, number];
  return { start: localMidnightUtc(y, m, d, tz), end: localMidnightUtc(y, m, d + 1, tz) };
}

function localMidnightUtc(year: number, month: number, day: number, tz: string): Date {
  // First guess assumes the current offset, then correct once (handles DST edges;
  // a no-op for Lagos, which is fixed UTC+1).
  let candidate = new Date(Date.UTC(year, month - 1, day));
  candidate = new Date(candidate.getTime() - tzOffsetMs(candidate, tz));
  const correction = tzOffsetMs(candidate, tz);
  const recheck = new Date(Date.UTC(year, month - 1, day) - correction);
  return recheck;
}

/** Human rendering in the user's timezone, e.g. "12 Mar 2026, 4:15 PM". */
export function formatInTz(instant: Date, tz: string = LAGOS_TZ): string {
  return new Intl.DateTimeFormat('en-NG', {
    timeZone: tz,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(instant);
}
